import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';

/**
 * Utilidades compartidas por el Mapa del organigrama. El diagrama clasico
 * (OrganizationChart) mantiene su propia logica; aqui solo reutilizamos la
 * fuente de verdad de colores y el fetch de la jerarquia anidada.
 */

// Paleta categorica de gerencias: tonos apagados que funcionan como anillo
// de avatar, riel de rama y relleno de barra sobre fondo claro. Las claves
// se comparan normalizadas (sin acentos, minusculas) via getGerenciaColor.
export const GERENCIA_BASE_COLOR = {
  'presidencia': '#2b3a4f', // tinta
  'gerencia general': '#6b7f94', // acero
  'comercial': '#b0532f', // terracota
  'supply chain': '#3d6b8f', // azul petroleo
  'finanzas': '#a8842c', // ocre
  'mercadeo': '#96527a', // ciruela
  'gestion humana': '#5f7d54', // oliva
  'ti': '#2f7f74', // verde azulado
  'sin gerencia': '#9aa5b1', // neutro para fallback
};

// Alias historicos o variantes de escritura que llegan desde datos viejos
const GERENCIA_ALIAS = {
  'it': 'ti',
  'rrhh': 'gestion humana',
};

export function normalizeGerencia(nombre) {
  const plain = (nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  return GERENCIA_ALIAS[plain] || plain;
}

export function getGerenciaColor(gerencia) {
  return (
    GERENCIA_BASE_COLOR[normalizeGerencia(gerencia)] ||
    GERENCIA_BASE_COLOR['sin gerencia']
  );
}

// Color ambar de las vacantes, unico en todas las vistas
export const VACANTE_COLOR = '#d97706';

// Tinte translucido de un hex, para rieles y fondos suaves
export function tintOf(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Clave estable por nodo: evita colisiones entre ids de empleado y vacante
export const nodeKey = (n) => `${n?.tipo || 'empleado'}-${n?.id}`;

// Orden de lectura dentro de una rama: personas primero, agrupadas por cargo
// (los del mismo cargo quedan juntos) y luego por nombre; vacantes al final
const porCargoYNombre = (a, b) =>
  (a.cargo || '').localeCompare(b.cargo || '', 'es') ||
  (a.nombre || '').localeCompare(b.nombre || '', 'es');

export function sortSubordinados(nodos) {
  const list = nodos || [];
  return [
    ...list.filter((n) => n.tipo !== 'vacante').sort(porCargoYNombre),
    ...list.filter((n) => n.tipo === 'vacante').sort(porCargoYNombre),
  ];
}

/**
 * Hook que trae la jerarquia anidada desde el backend, con los mismos
 * parametros que usa OrganizationChart. Solo dispara el fetch cuando
 * `enabled` es true (asi el modo Diagrama no genera un fetch duplicado).
 */
export function useJerarquia({ gerenciaId, departamentoId, areaId, enabled }) {
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // `settled` evita el parpadeo de "sin datos" antes de que arranque el fetch
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setRoots([]);
      setError(null);
      setLoading(false);
      setSettled(false);
      return undefined;
    }

    let cancelled = false;
    setSettled(false);
    setLoading(true);

    const fetchJerarquia = async () => {
      try {
        const params = new URLSearchParams();
        if (departamentoId) params.append('departamento_id', departamentoId);
        if (gerenciaId) params.append('gerencia_id', gerenciaId);
        if (areaId) params.append('area_id', areaId);
        const response = await axiosInstance.get(`/api/empleados/jerarquia?${params}`);
        if (cancelled) return;
        setRoots(response.data?.jerarquia || []);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error cargando jerarquia:', err);
        setError('No se pudo cargar la estructura. Intenta de nuevo.');
        setRoots([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSettled(true);
        }
      }
    };

    fetchJerarquia();
    return () => {
      cancelled = true;
    };
  }, [gerenciaId, departamentoId, areaId, enabled]);

  return { roots, loading: enabled ? loading || !settled : false, error };
}

// Estado compartido para carga / error / vacio dentro del lienzo
export function OrgStateBox({ spinner, text, tone, icon }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
      {spinner && (
        <div className="w-12 h-12 rounded-full border-[3px] border-gray-200 border-t-sura-blue animate-spin" />
      )}
      {icon && <div className="text-4xl text-gray-300">{icon}</div>}
      <p className={`text-sm font-medium ${tone === 'error' ? 'text-red-600' : 'text-gray-500'}`}>
        {text}
      </p>
    </div>
  );
}
